import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { removeProduct } from "../../../redux/reducer/cartReducer";
import { cldResize } from "../../../utils";
import { formatVnd, shortName } from "../../../utils/format";
import { useActivePromotions } from "../../../services/promotionServices";
import { BULK_MIN_QTY, BULK_RATE, FREESHIP_MIN, bulkDiscount, unitPrice } from "../../../utils/promotion";
import { priceCartLocal } from "../../../utils/promotionEngine";

/** Giỏ hàng thu nhỏ (dropdown khi rê chuột vào icon giỏ trên header desktop). */
const Cart = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart } = useSelector((state) => state.cartReducer);
  const items = cart ?? [];
  // Giá sau chương trình khuyến mãi (mua kèm / quà) — cùng engine với giỏ hàng & máy chủ
  const promoData = useActivePromotions();
  const priced = priceCartLocal(items, promoData);
  const total = priced.subtotal;
  const qty = items.reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0);
  const bulk = bulkDiscount(priced.details);
  const gifts = priced.details.filter((d) => d.isGift);

  return (
    <div className="w-[360px] overflow-hidden rounded-2xl bg-white shadow-pop ring-1 ring-black/5">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="m-0 text-lg font-semibold text-slate-900">Giỏ hàng</h2>
        <span className="text-sm text-slate-500">{items.length} sản phẩm</span>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <i className="bi bi-bag text-4xl text-slate-300" aria-hidden="true" />
          <p className="m-0 mt-2 text-base font-medium text-slate-700">Giỏ hàng đang trống</p>
          <p className="m-0 mt-1 text-sm text-slate-500">Khám phá sản phẩm bán chạy và thêm vào giỏ nhé.</p>
        </div>
      ) : (
        <>
          <ul className="m-0 max-h-[360px] list-none overflow-y-auto p-2">
            {items.map((it) => {
              const p = it?.product;
              return (
                <li key={p?._id} className="flex list-none items-start gap-3 rounded-xl p-2 hover:bg-slate-50">
                  <Link to={`/${p?.category}/${p?.url}`} onClick={onClose} className="flex min-w-0 flex-1 gap-3 text-inherit no-underline">
                    <img
                      src={cldResize(p?.images?.[0], 120)}
                      alt=""
                      width={56}
                      height={56}
                      className="h-14 w-14 flex-shrink-0 rounded-lg bg-white object-contain p-1 ring-1 ring-slate-100"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-base leading-snug text-slate-800">{p?.name}</span>
                      <span className="mt-1 flex items-center gap-1.5 text-base">
                        <span className="font-semibold text-sale-600">{formatVnd(unitPrice(p))}</span>
                        <span className="text-slate-400">× {it?.quantity}</span>
                      </span>
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => dispatch(removeProduct(p?._id))}
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-sale-50 hover:text-sale-600"
                    aria-label={`Xóa ${p?.name} khỏi giỏ`}
                  >
                    <i className="bi bi-trash3" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-slate-100 bg-slate-50/70 p-4">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-base text-slate-600">Tạm tính</span>
              <span className="text-xl font-bold text-slate-900">{formatVnd(total - bulk)}</span>
            </div>
            <p className={`m-0 text-sm ${bulk > 0 ? "text-emerald-700" : "text-amber-700"}`}>
              <i className="bi bi-gift mr-1" aria-hidden="true" />
              {bulk > 0
                ? `Đã giảm thêm ${Math.round(BULK_RATE * 100)}% mỗi sản phẩm: -${formatVnd(bulk)}`
                : `Mua thêm ${BULK_MIN_QTY - qty} sản phẩm để giảm thêm ${Math.round(BULK_RATE * 100)}% mỗi sản phẩm`}
            </p>
            {gifts.map((g) => (
              <p key={`gift-${g.promotionId}`} className="m-0 mt-0.5 text-sm font-medium text-emerald-700">
                <i className="bi bi-gift-fill mr-1" aria-hidden="true" />
                Quà tặng: {g.quantity} × {shortName(g.name)}
              </p>
            ))}
            <p className={`m-0 mb-3 mt-0.5 text-sm ${total >= FREESHIP_MIN ? "text-emerald-700" : "text-sky-700"}`}>
              <i className="bi bi-truck mr-1" aria-hidden="true" />
              {total >= FREESHIP_MIN ? "Được miễn phí giao hàng tiêu chuẩn" : `Mua thêm ${formatVnd(FREESHIP_MIN - total)} để được miễn phí giao hàng`}
            </p>
            <button
              type="button"
              onClick={() => {
                navigate("/gio-hang");
                onClose?.();
              }}
              className="h-11 w-full rounded-xl bg-brand-600 text-lg font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Xem giỏ hàng & thanh toán
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
